'use client';

import { useRef, useState } from 'react';
import { usePathname } from 'next/navigation';
import { QrCodeIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { QRCodeCanvas } from 'qrcode.react';
import { ButtonGhost } from '@/components/Buttons';

export interface QrTranslations {
  generateButton: string;
  hint: string;
  download: string;
}

// High resolution for crisp printing and retina screens; scaled to fit its container
const QR_SIZE = 1024;

export const QrCodeSection = ({ translations: t }: { translations: QrTranslations }) => {
  const [visible, setVisible] = useState(false);
  const pathname = usePathname();
  const canvasWrapperRef = useRef<HTMLDivElement>(null);

  // Only read on the client after a user click, so window is always available
  const pageUrl = visible ? `${window.location.origin}${pathname || '/donatii'}` : '';

  const handleDownload = () => {
    const canvas = canvasWrapperRef.current?.querySelector('canvas');
    if (!canvas) return;
    const link = document.createElement('a');
    link.href = canvas.toDataURL('image/png');
    link.download = 'k29-donatii-qr.png';
    link.click();
  };

  return (
    <div className="flex flex-col items-start gap-6">
      <button
        type="button"
        onClick={() => setVisible(!visible)}
        aria-expanded={visible}
        className="inline-flex items-center gap-x-2 text-sm text-gray-500 underline decoration-gray-300 underline-offset-4 transition-colors hover:text-gray-900 hover:decoration-gray-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-600 dark:text-gray-400 dark:decoration-white/30 dark:hover:text-white dark:hover:decoration-white/60"
      >
        <QrCodeIcon className="h-4 w-4" aria-hidden="true" />
        {t.generateButton}
      </button>

      {visible && (
        <div className="flex flex-col items-start gap-4">
          <div
            ref={canvasWrapperRef}
            className="w-full max-w-md rounded-lg bg-white p-5 shadow-sm ring-1 ring-gray-200 dark:ring-white/10"
          >
            <QRCodeCanvas
              value={pageUrl}
              size={QR_SIZE}
              level="H"
              marginSize={1}
              imageSettings={{
                src: '/k29-logo.svg',
                width: 200,
                height: 152,
                excavate: true,
              }}
              style={{ width: '100%', height: 'auto' }}
            />
          </div>
          <p className="text-xs leading-5 text-gray-500 dark:text-gray-400">{t.hint}</p>
          <ButtonGhost
            type="button"
            onClick={handleDownload}
            className="!text-gray-600 hover:!bg-gray-100 dark:!text-gray-300 dark:hover:!bg-white/10"
          >
            <ArrowDownTrayIcon className="h-5 w-5" aria-hidden="true" />
            {t.download}
          </ButtonGhost>
        </div>
      )}
    </div>
  );
};
